import Backend from 'App/Dataman-Backend';
import { apiGet, apiPost, apiPatch, apiDelete } from '@/app/api/core/http';
import { STATS_CATEGORIES_MAP } from 'App/utils/Collections';
import { USERS_PATHS } from './paths';

const LD_HEADERS = { Accept: 'application/ld+json' };

const staffStatsEntries = Object.keys(STATS_CATEGORIES_MAP).map((cat) => [
  cat,
  {
    getGroupedList: (params) =>
      apiGet(`${USERS_PATHS.staffStats[cat]}_group_by`, {
        params,
        headers: LD_HEADERS,
      }),
    getList: (params) =>
      apiGet(USERS_PATHS.staffStats[cat], {
        params,
        headers: LD_HEADERS,
      }),
    getItem: (id, params) => apiGet(`${USERS_PATHS.staffStats[cat]}/${id}`, { params }),
  },
]);

const staffStatsItemEntries = Object.keys(STATS_CATEGORIES_MAP).map((cat) => [
  cat,
  {
    getList: (params) =>
      apiGet(USERS_PATHS.staffStatsItem[cat], {
        params,
        headers: LD_HEADERS,
      }),
    getItem: (id, params) => apiGet(`${USERS_PATHS.staffStatsItem[cat]}/${id}`, { params }),
  },
]);

export const usersApiAdapter = {
  users: {
    getList: (params, config = {}) => apiGet(USERS_PATHS.users, { params, is_protected: true, ld: true, ...config }),
    getItem: (id) => apiGet(`${USERS_PATHS.users}/${id}`),
    getRoles: (params) => apiGet(`${USERS_PATHS.roles}`, { params }),
    update: (id, data) => apiPatch(`${USERS_PATHS.users}/${id}`, { data }),
    create: (data) => apiPost(`${USERS_PATHS.users}`, { data }),
    getStaffStatistics: (params) => apiGet(USERS_PATHS.staffStatistics, { params }),
    getUserStats: async (id, params) =>
      apiGet(`${USERS_PATHS.users}/${id}/staff_statistics`, { params }).then((res) => res.data || null),
    getUserProductivity: async (id, params) =>
      apiGet(`${USERS_PATHS.users}/${id}/staff_productivity`, { params }).then((res) => res.data || null),
    getUserTimeConsumption: async (id, params) =>
      apiGet(`${USERS_PATHS.users}/${id}/staff_time_consumption`, { params }).then((res) => res.data || null),
  },
  usergroups: {
    getList: (params) =>
      apiGet(USERS_PATHS.userGroups, {
        params,
        headers: LD_HEADERS,
      }),
  },
  accessLog: {
    getAccessLogAsXLSFile: (userId, params) =>
      apiGet(`${USERS_PATHS.accessLog}/${userId}/xls`, {
        params,
        responseType: 'blob',
      }),
  },
  staffStats: Object.fromEntries(staffStatsEntries),
  staffStatsItem: Object.fromEntries(staffStatsItemEntries),
};

export const userService = {
  getToken: async (data) => {
    // eslint-disable-next-line no-unused-vars
    const { token, ...credentials } = data || {};
    const response = await Backend.post({
      url: USERS_PATHS.login,
      data: credentials,
      auth: false,
      is_protected: true,
    });
    Backend.setToken(response.data);
    return response;
  },
  getLocalToken: () => {
    const json = localStorage.getItem('wims_token') || '';
    let data;
    try {
      data = JSON.parse(json) || {};
    } catch (e) {
      data = {};
    }
    return data;
  },
  setLocalToken: (data) => localStorage.setItem('wims_token', JSON.stringify(data)),
  clearLocalToken: () => localStorage.removeItem('wims_token'),
  setToken: Backend.setToken,
  refreshToken: Backend.refreshToken,
  getUser: (data) => apiGet(USERS_PATHS.user, { data }),
  updateFilter: (id, data) => apiPatch(`${USERS_PATHS.userFilters}/${id}`, { data, is_protected: true }),
  subscribeUsersToFilter: (filterID, roleId) =>
    apiPost(`${USERS_PATHS.userFilters}/${filterID}/batch_clone`, { data: { roleId } }),

  getFilters: (params, conf = {}) => apiGet(USERS_PATHS.userFilters, { params, ...conf }),
  createFilters: (data) => apiPost(USERS_PATHS.userFilters, { data, is_protected: true }),
  deleteFilter: (id) => apiDelete(`${USERS_PATHS.userFilters}/${id}`, { is_protected: true }),
  subscribeToFilter: (id) => apiPost(`${USERS_PATHS.userFilters}/${id}/clone`),

  getBookmarks: (params) => apiGet(USERS_PATHS.userBookmarks + USERS_PATHS.suffix, { params }),
  createBookmarks: (data) => apiPost(USERS_PATHS.userBookmarks, { data, is_protected: true }),
  deleteBookmark: (id) => apiDelete(`${USERS_PATHS.userBookmarks}/${id}`, { is_protected: true }),
  updProfile: (id, data) => apiPatch(`${USERS_PATHS.users}/${id}`, { data }),
  changePassword: (data) => apiPost(USERS_PATHS.changePassword, { data }),
};

export const passwordResetService = {
  request: (data) => apiPost(USERS_PATHS.resetPasswordRequest, { data, is_protected: true, auth: false }),
  reset: (data) => apiPost(USERS_PATHS.resetPasswordReset, { data, is_protected: true, auth: false }),
};
